import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  adminSignIn,
  fetchAdminUserById,
  isAdminRole,
  resendAdminLoginOtp,
  verifyAdminLoginOtp,
} from '../services/api/admin';
import {
  acceptCompanyClaimInvitation,
  companyOwnerSignIn,
  fetchCompanyPayoutDetails,
  fetchCompanyOwnerById,
  fetchMyCompany,
  previewCompanyClaimInvitation,
  resendCompanyOwnerLoginOtp,
  verifyCompanyOwnerLoginOtp,
} from '../services/api/companyOwnerAuth';
import { customerSignIn, getGoogleSignUpUrl } from '../services/api/customerAuth';
import { driverSignIn, fetchDriverByEmail, getDriverGoogleSignUpUrl } from '../services/api/driverAuth';
import {
  clearCompanyOwnerClaimToken,
  clearCompanyOwnerAuthFlow,
  getCompanyOwnerClaimToken,
  saveCompanyOwnerAuthTokens,
  saveCompanyOwnerAuthUser,
  saveCompanyOwnerClaimToken,
  saveCompanyOwnerCompany,
  saveCompanyOwnerPayoutDetails,
} from '../services/storage/companyOwnerFlow';
import { clearAdminAuthFlow, saveAdminAuthTokens, saveAdminAuthUser } from '../services/storage/adminFlow';
import {
  clearCustomerAuthFlow,
  saveCustomerAuthTokens,
  saveCustomerAuthUser,
} from '../services/storage/customerFlow';
import {
  clearDriverAuthFlow,
  saveDriverAuthTokens,
  saveDriverAuthUser,
} from '../services/storage/driverFlow';
import { nextStageForAdmin } from '../utils/adminOnboarding';
import { nextStageForCustomer } from '../utils/customerOnboarding';
import { formatDateTime, getCompanyOwnerNextRoute } from '../utils/companyOwner';
import { nextStageForDriver } from '../utils/driverOnboarding';
import { CompanyClaimInvitationPreview, CompanyOwnerAuthResponse } from '../types/companyOwner';
import Logo from '../components/common/Logo';
import '../styles/register.css';
import '../styles/route.css';

type LoginRole = 'customer' | 'driver' | 'company';

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roleParam = searchParams.get('role');
  const emailParam = searchParams.get('email')?.trim() || '';
  const resetParam = searchParams.get('reset');
  const queryClaimToken = searchParams.get('claimToken')?.trim() || '';
  const initialRole: LoginRole =
    roleParam === 'driver'
      ? 'driver'
      : roleParam === 'company'
        ? 'company'
        : 'customer';
  const [selectedRole, setSelectedRole] = useState<LoginRole>(initialRole);
  const [claimToken, setClaimToken] = useState(queryClaimToken || getCompanyOwnerClaimToken() || '');
  const [claimPreview, setClaimPreview] = useState<CompanyClaimInvitationPreview | null>(null);
  const [claimPreviewLoading, setClaimPreviewLoading] = useState(Boolean(queryClaimToken || getCompanyOwnerClaimToken()));
  const [claimPreviewError, setClaimPreviewError] = useState('');
  const [email, setEmail] = useState(emailParam);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [companyOtpCode, setCompanyOtpCode] = useState('');
  const [companyOtpError, setCompanyOtpError] = useState('');
  const [companyOtpMessage, setCompanyOtpMessage] = useState('');
  const [companyOtpLoading, setCompanyOtpLoading] = useState(false);
  const [companyOtpResending, setCompanyOtpResending] = useState(false);
  const [showCompanyOtpModal, setShowCompanyOtpModal] = useState(false);
  const [pendingCompanyLogin, setPendingCompanyLogin] = useState<{
    email: string;
    password: string;
  } | null>(null);
  const [adminOtpCode, setAdminOtpCode] = useState('');
  const [adminOtpError, setAdminOtpError] = useState('');
  const [adminOtpMessage, setAdminOtpMessage] = useState('');
  const [adminOtpLoading, setAdminOtpLoading] = useState(false);
  const [adminOtpResending, setAdminOtpResending] = useState(false);
  const [showAdminOtpModal, setShowAdminOtpModal] = useState(false);
  const [pendingAdminLogin, setPendingAdminLogin] = useState<{
    email: string;
    challengeId: string;
    maskedDestination: string | null;
    challengeExpiresAt: string | null;
  } | null>(null);

  const roleMeta = useMemo(
    () => ({
      customer: {
        badge: 'Customer Login',
        title: 'Log in to your ShiftWagon account',
        copy: 'Continue onboarding or get back to delivery booking from the next customer step.',
        submitLabel: 'Log in',
      },
      driver: {
        badge: 'Driver Login',
        title: 'Log in to your ShiftWagon driver account',
        copy: 'Continue driver onboarding on web, or move to the ready screen once your onboarding has been submitted.',
        submitLabel: 'Log in as driver',
      },
      company: {
        badge: 'Company Owner Login',
        title: claimToken
          ? 'Log in to claim your invited company'
          : 'Log in to your company owner account',
        copy: claimToken
          ? 'Sign in with the invited company email address. We will accept the invitation and attach your account automatically after authentication.'
          : 'Open company onboarding if your company profile is not yet claimed, or move into the dashboard if it is ready.',
        submitLabel: 'Log in as company owner',
      },
    }),
    [claimToken],
  );

  const meta = roleMeta[selectedRole];
  const googleAuthUrl = selectedRole === 'driver' ? getDriverGoogleSignUpUrl() : getGoogleSignUpUrl();
  const companyRegisterPath = claimToken
    ? `/register?role=company&claimToken=${encodeURIComponent(claimToken)}`
    : '/register?role=company';

  useEffect(() => {
    if (!claimToken) {
      setClaimPreview(null);
      setClaimPreviewLoading(false);
      setClaimPreviewError('');
      return;
    }

    saveCompanyOwnerClaimToken(claimToken);
    let mounted = true;

    const loadPreview = async () => {
      setClaimPreviewLoading(true);
      setClaimPreviewError('');

      try {
        const preview = await previewCompanyClaimInvitation(claimToken);
        if (!mounted) return;
        setClaimPreview(preview);
      } catch (previewError: any) {
        if (!mounted) return;
        setClaimPreview(null);
        setClaimPreviewError(previewError.message || 'Could not load the invitation preview.');
      } finally {
        if (mounted) {
          setClaimPreviewLoading(false);
        }
      }
    };

    void loadPreview();

    return () => {
      mounted = false;
    };
  }, [claimToken]);

  const finishAdminLogin = async (response: {
    success: boolean;
    message: string;
    token?: string;
    accessToken?: string | null;
    refreshToken?: string | null;
    accessTokenExpiresAt?: string | null;
    refreshTokenExpiresAt?: string | null;
    user?: any;
  }) => {
    const accessToken = response.accessToken ?? response.token;
    if (!response.success || !response.user || !accessToken || !response.refreshToken) {
      throw new Error(response.message || 'Could not sign in as admin.');
    }

    if (!isAdminRole(response.user.roles)) {
      throw new Error('This account is not registered as an admin.');
    }

    const adminUser =
      response.user.id
        ? await fetchAdminUserById(response.user.id, accessToken).catch(() => response.user as any)
        : response.user;

    clearCustomerAuthFlow();
    clearDriverAuthFlow();
    clearCompanyOwnerAuthFlow();
    clearAdminAuthFlow();
    saveAdminAuthTokens({
      accessToken,
      refreshToken: response.refreshToken,
      accessTokenExpiresAt: response.accessTokenExpiresAt,
      refreshTokenExpiresAt: response.refreshTokenExpiresAt,
    });
    saveAdminAuthUser(adminUser);
    navigate(nextStageForAdmin(adminUser) === 'completed' ? '/admin' : '/admin/onboarding');
  };

  const completeCompanyOwnerLogin = async (response: CompanyOwnerAuthResponse) => {
    const accessToken = response.accessToken ?? response.token;
    if (!response.success || !accessToken || !response.refreshToken) {
      throw new Error(response.message || 'Could not sign in.');
    }

    const companyOwnerUser =
      response.userResponse?.id
        ? await fetchCompanyOwnerById(response.userResponse.id, accessToken)
        : response.userResponse;

    if (!companyOwnerUser) {
      throw new Error('Could not load the company owner account.');
    }

    clearCustomerAuthFlow();
    clearDriverAuthFlow();
    clearAdminAuthFlow();
    saveCompanyOwnerAuthTokens({
      accessToken,
      refreshToken: response.refreshToken,
      accessTokenExpiresAt: response.accessTokenExpiresAt,
      refreshTokenExpiresAt: response.refreshTokenExpiresAt,
    });
    saveCompanyOwnerAuthUser(companyOwnerUser);

    const activeClaimToken = claimToken || getCompanyOwnerClaimToken();
    let company;
    if (activeClaimToken) {
      try {
        company = await acceptCompanyClaimInvitation(activeClaimToken);
      } catch (claimError: any) {
        throw new Error(
          `Login verified, but we could not attach this company invitation. ${
            claimError.message || 'Please contact ShiftWagon support to review the claim.'
          }`,
        );
      }
    } else {
      company = await fetchMyCompany();
    }

    if (company) {
      saveCompanyOwnerCompany(company);
      try {
        const payoutDetails = await fetchCompanyPayoutDetails();
        saveCompanyOwnerPayoutDetails(payoutDetails);
        navigate(getCompanyOwnerNextRoute(company, payoutDetails));
      } catch {
        navigate(getCompanyOwnerNextRoute(company));
      }
    } else {
      navigate(getCompanyOwnerNextRoute(company));
    }

    if (activeClaimToken) {
      clearCompanyOwnerClaimToken();
      setClaimToken('');
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    setError('');
    setLoading(true);

    try {
      const normalizedEmail = email.trim().toLowerCase();

      if (selectedRole === 'customer') {
        const response = await customerSignIn({
          email: normalizedEmail,
          password,
        });

        if (response.otpRequired) {
          const looksLikeHiddenAdminLogin =
            Boolean(response.challengeId) ||
            /admin login/i.test(response.message || '') ||
            /otp verification required/i.test(response.message || '');

          if (looksLikeHiddenAdminLogin) {
            if (response.challengeId) {
              setPendingAdminLogin({
                email: normalizedEmail,
                challengeId: response.challengeId,
                maskedDestination: response.maskedDestination ?? null,
                challengeExpiresAt: response.challengeExpiresAt ?? null,
              });
              setAdminOtpCode('');
              setAdminOtpError('');
              setAdminOtpMessage('');
              setShowAdminOtpModal(true);
              return;
            }

            const adminResponse = await adminSignIn({
              email: normalizedEmail,
              password,
            });

            if (adminResponse.otpRequired && adminResponse.challengeId) {
              setPendingAdminLogin({
                email: normalizedEmail,
                challengeId: adminResponse.challengeId,
                maskedDestination: adminResponse.maskedDestination ?? null,
                challengeExpiresAt: adminResponse.challengeExpiresAt ?? null,
              });
              setAdminOtpCode('');
              setAdminOtpError('');
              setAdminOtpMessage('');
              setShowAdminOtpModal(true);
              return;
            }

            await finishAdminLogin(adminResponse);
            return;
          }

          throw new Error(response.message || 'Additional login verification is required for this account.');
        }

        const accessToken = response.accessToken ?? response.token;
        if (!response.success || !response.userResponse || !accessToken || !response.refreshToken) {
          throw new Error(response.message || 'Could not sign in.');
        }

        clearDriverAuthFlow();
        clearCompanyOwnerAuthFlow();
        clearAdminAuthFlow();
        saveCustomerAuthTokens({
          accessToken,
          refreshToken: response.refreshToken,
          accessTokenExpiresAt: response.accessTokenExpiresAt,
          refreshTokenExpiresAt: response.refreshTokenExpiresAt,
        });
        saveCustomerAuthUser(response.userResponse);

        const nextStage = nextStageForCustomer(response.userResponse);
        navigate(
          nextStage === 'completed' ? '/schedule-booking' : '/register?resume=login&role=customer',
        );
        return;
      }

      if (selectedRole === 'driver') {
        const response = await driverSignIn({
          email: normalizedEmail,
          password,
        });

        const accessToken = response.accessToken ?? response.token;
        if (!response.success || !response.driverResponse || !accessToken || !response.refreshToken) {
          throw new Error(response.message || 'Could not sign in.');
        }

        clearCustomerAuthFlow();
        clearCompanyOwnerAuthFlow();
        clearAdminAuthFlow();
        saveDriverAuthTokens({
          accessToken,
          refreshToken: response.refreshToken,
          accessTokenExpiresAt: response.accessTokenExpiresAt,
          refreshTokenExpiresAt: response.refreshTokenExpiresAt,
        });
        const hydratedDriver =
          (await fetchDriverByEmail(response.driverResponse.email, accessToken).catch(
            () => response.driverResponse,
          )) ?? response.driverResponse;
        saveDriverAuthUser(hydratedDriver);

        const nextStage = nextStageForDriver(hydratedDriver);
        navigate(
          nextStage === 'completed'
            ? '/driver/account-ready'
            : '/register?resume=login&role=driver',
        );
        return;
      }

      const response = await companyOwnerSignIn({
        email: normalizedEmail,
        password,
        role: 'company',
      });

      if (response.emailOtpRequired) {
        setPendingCompanyLogin({ email: normalizedEmail, password });
        setCompanyOtpCode('');
        setCompanyOtpError('');
        setCompanyOtpMessage('');
        setShowCompanyOtpModal(true);
        return;
      }

      await completeCompanyOwnerLogin(response);
    } catch (loginError: any) {
      setError(loginError.message || 'Could not sign in.');
    } finally {
      setLoading(false);
    }
  };

  const handleCompanyOtpSubmit = async () => {
    if (!pendingCompanyLogin) {
      setCompanyOtpError('Please enter your email and password again.');
      return;
    }

    if (companyOtpCode.trim().length !== 6) {
      setCompanyOtpError('Enter the 6-digit code from your email.');
      return;
    }

    setCompanyOtpLoading(true);
    setCompanyOtpError('');
    setCompanyOtpMessage('');

    try {
      const response = await verifyCompanyOwnerLoginOtp({
        email: pendingCompanyLogin.email,
        password: pendingCompanyLogin.password,
        code: companyOtpCode.trim(),
      });
      await completeCompanyOwnerLogin(response);
    } catch (otpError: any) {
      setCompanyOtpError(otpError.message || 'Could not verify the login code.');
    } finally {
      setCompanyOtpLoading(false);
    }
  };

  const handleCompanyOtpResend = async () => {
    if (!pendingCompanyLogin) {
      setCompanyOtpError('Please enter your email and password again.');
      return;
    }

    setCompanyOtpResending(true);
    setCompanyOtpError('');
    setCompanyOtpMessage('');

    try {
      await resendCompanyOwnerLoginOtp({
        email: pendingCompanyLogin.email,
        password: pendingCompanyLogin.password,
        role: 'company',
      });
      setCompanyOtpCode('');
      setCompanyOtpMessage('A new 6-digit code has been sent to your email.');
    } catch (resendError: any) {
      setCompanyOtpError(resendError.message || 'Could not resend the login code.');
    } finally {
      setCompanyOtpResending(false);
    }
  };

  const handleCloseCompanyOtpModal = () => {
    setShowCompanyOtpModal(false);
    setPendingCompanyLogin(null);
    setCompanyOtpCode('');
    setCompanyOtpError('');
    setCompanyOtpMessage('');
  };

  const handleAdminOtpSubmit = async () => {
    if (!pendingAdminLogin?.challengeId) {
      setAdminOtpError('Please sign in again to request a new admin login code.');
      return;
    }

    if (adminOtpCode.trim().length !== 6) {
      setAdminOtpError('Enter the 6-digit code sent to your phone.');
      return;
    }

    setAdminOtpLoading(true);
    setAdminOtpError('');
    setAdminOtpMessage('');

    try {
      const response = await verifyAdminLoginOtp({
        challengeId: pendingAdminLogin.challengeId,
        code: adminOtpCode.trim(),
      });
      await finishAdminLogin(response);
      setShowAdminOtpModal(false);
      setPendingAdminLogin(null);
    } catch (otpError: any) {
      setAdminOtpError(otpError.message || 'Could not verify the login code.');
    } finally {
      setAdminOtpLoading(false);
    }
  };

  const handleAdminOtpResend = async () => {
    if (!pendingAdminLogin?.challengeId) {
      setAdminOtpError('Please sign in again to request a new admin login code.');
      return;
    }

    setAdminOtpResending(true);
    setAdminOtpError('');
    setAdminOtpMessage('');

    try {
      const response = await resendAdminLoginOtp({
        challengeId: pendingAdminLogin.challengeId,
      });

      if (!response.challengeId) {
        throw new Error(response.message || 'Could not resend the login code.');
      }

      setPendingAdminLogin((current) =>
        current
          ? {
              ...current,
              challengeId: response.challengeId ?? current.challengeId,
              maskedDestination: response.maskedDestination ?? current.maskedDestination,
              challengeExpiresAt: response.challengeExpiresAt ?? current.challengeExpiresAt,
            }
          : current,
      );
      setAdminOtpCode('');
      setAdminOtpMessage(response.message || 'A new 6-digit code has been sent to your phone.');
    } catch (resendError: any) {
      setAdminOtpError(resendError.message || 'Could not resend the login code.');
    } finally {
      setAdminOtpResending(false);
    }
  };

  const handleCloseAdminOtpModal = () => {
    setShowAdminOtpModal(false);
    setPendingAdminLogin(null);
    setAdminOtpCode('');
    setAdminOtpError('');
    setAdminOtpMessage('');
  };

  return (
    <main className="register-page">
      {/* The form panel comes first in source so this page's h1 is the first
          heading a screen reader meets; the brand panel is pulled back to the
          left column visually with `order`. Do not reorder these in the DOM to
          "fix" the layout — that would put the brand h2 ahead of the h1 again. */}
      <div className="register-shell register-shell-brand-second">
        <div className="register-panel register-panel-form">
          <div className="register-form-header">
            <div className="register-form-heading-row">
              <div className="register-form-badge">{meta.badge}</div>
              <Link className="register-home-link" to="/">
                &larr; Home
              </Link>
            </div>
            <h1 className="register-form-title">{meta.title}</h1>
            <p className="register-form-copy">{meta.copy}</p>
          </div>

          {selectedRole === 'company' && claimToken ? (
            <div className="register-highlight-card register-highlight-card-light" style={{ marginBottom: '24px' }}>
              <div className="register-highlight-row">
                <span className="register-highlight-icon">C</span>
                <div>
                  <div className="register-highlight-title">Company Claim Invitation</div>
                  <div className="register-highlight-note">
                    {claimPreviewLoading
                      ? 'Loading invitation...'
                      : claimPreview?.companyName || 'Invitation pending'}
                  </div>
                </div>
              </div>
              {claimPreviewLoading ? (
                <p className="register-highlight-copy">
                  Checking the invitation details before login.
                </p>
              ) : claimPreview ? (
                <>
                  <p className="register-highlight-copy">
                    You&apos;ve been invited to claim {claimPreview.companyName} on ShiftWagon.
                  </p>
                  <div className="register-benefit-list" style={{ marginTop: '18px' }}>
                    <div className="register-benefit-item">
                      <span className="register-benefit-dot" />
                      <span>Invited email: {claimPreview.maskedCompanyEmail}</span>
                    </div>
                    <div className="register-benefit-item">
                      <span className="register-benefit-dot" />
                      <span>
                        Expires: {claimPreview.expiresAt ? formatDateTime(claimPreview.expiresAt) : 'Not available'}
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <div className="register-inline-error">{claimPreviewError}</div>
              )}
            </div>
          ) : null}

          <div className="register-role-grid" style={{ marginBottom: '24px' }}>
            {(['customer', 'driver', 'company'] as LoginRole[]).map((role) => {
              const active = role === selectedRole;
              return (
                <button
                  className={`register-role-card${active ? ' register-role-card-active' : ''}`}
                  key={role}
                  onClick={() => {
                    setSelectedRole(role);
                    setError('');
                  }}
                  type="button"
                >
                  <span className="register-role-icon">
                    {role === 'customer' ? 'U' : role === 'driver' ? 'D' : 'C'}
                  </span>
                  <span className="register-role-text">
                    <span className="register-role-title">
                      {role === 'customer'
                        ? 'Customer'
                        : role === 'driver'
                          ? 'Driver'
                          : 'Company Owner'}
                    </span>
                    <span className="register-role-description">
                      {role === 'customer'
                        ? 'Book trucks and manage deliveries.'
                        : role === 'driver'
                          ? 'Continue onboarding and receive assignments.'
                          : 'Claim your company and manage driver visibility.'}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>

          <form className="register-form" onSubmit={handleSubmit}>
            <div className="register-field-group">
              <div className="register-field">
                <label className="register-label" htmlFor="loginEmail">
                  Email Address
                </label>
                <input
                  autoCapitalize="none"
                  className="register-input"
                  id="loginEmail"
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="john@example.com"
                  required
                  type="email"
                  value={email}
                />
              </div>

              <div className="register-field">
                <label className="register-label" htmlFor="loginPassword">
                  Password
                </label>
                <div className="register-password-shell">
                  <input
                    className="register-input register-input-password"
                    id="loginPassword"
                    onChange={(event) => setPassword(event.target.value)}
                    placeholder="Enter your password"
                    required
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                  />
                  <button
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    className="register-password-toggle"
                    onClick={() => setShowPassword((current) => !current)}
                    type="button"
                  >
                    {showPassword ? (
                      <svg aria-hidden="true" className="register-password-icon" fill="none" viewBox="0 0 24 24">
                        <path d="M3 3l18 18" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                        <path d="M10.58 10.58A2 2 0 0013.42 13.42" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                        <path d="M9.88 5.09A10.94 10.94 0 0112 4.9c5.05 0 8.27 4.13 9 5.1a15.63 15.63 0 01-4.05 3.81" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                        <path d="M6.61 6.61C4.62 7.84 3.32 9.51 3 10c.5.66 2.15 2.79 4.82 4.13A11.8 11.8 0 0012 15.1c.64 0 1.25-.05 1.83-.14" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                      </svg>
                    ) : (
                      <svg aria-hidden="true" className="register-password-icon" fill="none" viewBox="0 0 24 24">
                        <path d="M2.46 12.29C3.74 10.18 6.89 6 12 6s8.26 4.18 9.54 6.29a.56.56 0 010 .58C20.26 14.98 17.11 19 12 19s-8.26-4.02-9.54-6.13a.56.56 0 010-.58z" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                        <circle cx="12" cy="12.5" r="3" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.8" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div className="register-secondary-actions register-secondary-actions-right">
              <Link
                className="register-login-link"
                to={`/forgot-password?role=${selectedRole}${email.trim() ? `&email=${encodeURIComponent(email.trim().toLowerCase())}` : ''}`}
              >
                Forgot password?
              </Link>
            </div>

            {resetParam === 'success' ? (
              <div className="register-inline-success">
                Password updated. Sign in with your new password.
              </div>
            ) : null}

            {error ? <div className="register-inline-error">{error}</div> : null}

            <button className="register-submit" disabled={loading} type="submit">
              {loading ? 'Signing in...' : meta.submitLabel}
            </button>

            {selectedRole !== 'company' ? (
              <>
                <div className="register-divider">
                  <span>or</span>
                </div>

                <a className="register-google-button" href={googleAuthUrl}>
                  <svg aria-hidden="true" className="register-google-icon" viewBox="0 0 18 18">
                    <path
                      d="M17.64 9.2c0-.64-.06-1.25-.16-1.84H9v3.48h4.84a4.14 4.14 0 0 1-1.8 2.72v2.26h2.92c1.7-1.57 2.68-3.88 2.68-6.62z"
                      fill="#4285F4"
                    />
                    <path
                      d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.92-2.26c-.8.54-1.84.86-3.04.86-2.34 0-4.33-1.58-5.04-3.7H.94v2.33A9 9 0 0 0 9 18z"
                      fill="#34A853"
                    />
                    <path
                      d="M3.96 10.72A5.41 5.41 0 0 1 3.68 9c0-.6.1-1.18.28-1.72V4.95H.94A9 9 0 0 0 0 9c0 1.45.34 2.82.94 4.05l3.02-2.33z"
                      fill="#FBBC05"
                    />
                    <path
                      d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.66 8.66 0 0 0 9 0 9 9 0 0 0 .94 4.95l3.02 2.33C4.67 5.16 6.66 3.58 9 3.58z"
                      fill="#EA4335"
                    />
                  </svg>
                  <span>Continue with Google</span>
                </a>
              </>
            ) : null}

            <p className="register-login-copy">
              Don&apos;t have an account?{' '}
              <Link
                className="register-login-link"
                to={
                  selectedRole === 'company'
                    ? companyRegisterPath
                    : `/register?role=${selectedRole}`
                }
              >
                Create one
              </Link>
            </p>
          </form>
        </div>

        {/* Last in source, first on screen — see the note on .register-shell. */}
        <aside className="register-panel register-panel-brand sw-on-dark">
          <div className="register-brand-lockup">
            <Logo variant="inverted" size={36} />
            <h2 className="register-brand-title">
              Welcome back.
              <br />
              Let&apos;s get moving.
            </h2>
            <p className="register-brand-copy">
              One login for customers, drivers and company owners — pick your role and
              continue where you left off.
            </p>
          </div>

          <p className="register-brand-footnote">Too big for the car? Book a bakkie.</p>
        </aside>
      </div>
      {showCompanyOtpModal ? (
        <div className="register-modal-backdrop" role="presentation">
          <div className="register-modal" role="dialog" aria-modal="true" aria-labelledby="company-login-otp-title">
            <button
              aria-label="Close login verification"
              className="register-modal-close"
              onClick={handleCloseCompanyOtpModal}
              type="button"
            >
              &times;
            </button>
            <div className="register-status-badge">Login Verification</div>
            <h2 className="register-modal-title" id="company-login-otp-title">
              Enter your company owner login code
            </h2>
            <p className="register-modal-copy">
              We sent a 6-digit code to {pendingCompanyLogin?.email || 'your email address'}.
            </p>
            <input
              className="register-input register-modal-input"
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => {
                setCompanyOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                setCompanyOtpError('');
              }}
              placeholder="123456"
              value={companyOtpCode}
            />
            {companyOtpMessage ? <div className="register-inline-success">{companyOtpMessage}</div> : null}
            {companyOtpError ? <div className="register-inline-error">{companyOtpError}</div> : null}
            <div className="register-modal-actions">
              <button className="register-submit register-submit-inline" disabled={companyOtpLoading || companyOtpResending} onClick={handleCompanyOtpSubmit} type="button">
                {companyOtpLoading ? 'Verifying...' : 'Verify and continue'}
              </button>
              <button
                className="register-text-button"
                disabled={companyOtpLoading || companyOtpResending}
                onClick={handleCompanyOtpResend}
                type="button"
              >
                {companyOtpResending ? 'Sending...' : 'Resend code'}
              </button>
              <button
                className="register-text-button"
                disabled={companyOtpLoading || companyOtpResending}
                onClick={handleCloseCompanyOtpModal}
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
      {showAdminOtpModal ? (
        <div className="register-modal-backdrop" role="presentation">
          <div className="register-modal" role="dialog" aria-modal="true" aria-labelledby="admin-login-otp-title">
            <button
              aria-label="Close admin login verification"
              className="register-modal-close"
              onClick={handleCloseAdminOtpModal}
              type="button"
            >
              &times;
            </button>
            <div className="register-status-badge">Verification</div>
            <h2 className="register-modal-title" id="admin-login-otp-title">
              Enter the OTP from your phone
            </h2>
            <p className="register-modal-copy">
              We sent a 6-digit code to {pendingAdminLogin?.maskedDestination || 'your verified phone number'}.
            </p>
            {pendingAdminLogin?.challengeExpiresAt ? (
              <p className="register-modal-copy" style={{ marginTop: '-6px' }}>
                This code expires at {formatDateTime(pendingAdminLogin.challengeExpiresAt)}.
              </p>
            ) : null}
            <input
              className="register-input register-modal-input"
              inputMode="numeric"
              maxLength={6}
              onChange={(event) => {
                setAdminOtpCode(event.target.value.replace(/\D/g, '').slice(0, 6));
                setAdminOtpError('');
              }}
              placeholder="123456"
              value={adminOtpCode}
            />
            {adminOtpMessage ? <div className="register-inline-success">{adminOtpMessage}</div> : null}
            {adminOtpError ? <div className="register-inline-error">{adminOtpError}</div> : null}
            <div className="register-modal-actions">
              <button className="register-submit register-submit-inline" disabled={adminOtpLoading || adminOtpResending} onClick={handleAdminOtpSubmit} type="button">
                {adminOtpLoading ? 'Verifying...' : 'Verify and continue'}
              </button>
              <button
                className="register-text-button"
                disabled={adminOtpLoading || adminOtpResending}
                onClick={handleAdminOtpResend}
                type="button"
              >
                {adminOtpResending ? 'Sending...' : 'Resend code'}
              </button>
              <button
                className="register-text-button"
                disabled={adminOtpLoading || adminOtpResending}
                onClick={handleCloseAdminOtpModal}
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
};

export default LoginPage;
